import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import CollectionsContext from '../../../services/CollectionsContext';
import { openFeatureWall } from '../../../services/payWallModal';
import { openSignup } from '../../../services/regModal';
import AccountContext from '../../../services/AccountContext';
import dynamic from 'next/dynamic';
import styles from './MultiSelectActions.module.less';
import { Tooltip } from 'edcom-ui';
import track from '../../../services/track';

const AssignmentsModal = dynamic(() => import('../../AssignmentsModal'), {
  ssr: false,
});

export default function MultiSelectActions({ selected }) {
  const { isPremium, memberId, ready } = useContext(AccountContext);
  const { setModalIsOpen, setSelectedContent } = useContext(CollectionsContext);
  const [assignmentsModalIsOpen, setAssignmentsModalIsOpen] = useState(false);
  const [assignmentsToAdd, setAssignmentsToAdd] = useState([]);

  const handlePrintAll = () => {
    if (!ready) {
      return;
    }

    // Make "Print All" feature only available to premium users.
    if (isPremium) {
      let selectedFormatted = [];
      const printableTypes = ['worksheet'];
      selected.forEach(content => {
        if (printableTypes.includes(content.type)) {
          selectedFormatted.push({
            type: content.type,
            id: String(content.id),
          });
        }
      });

      if (selectedFormatted.length > 0) {
        let url = '/api/collections/printContent?content=';
        for (let i = 0; i < selectedFormatted.length; i++) {
          url +=
            selectedFormatted[i].type + '|' + selectedFormatted[i].id + ',';
        }
        window.open(url, '_blank');

        track('Downloaded Set', {
          section: 'Search Results Page',
          count: selected.filter(s => s.type === 'worksheet').length,
        });
      } else {
        alert('Nothing is printable in your selected contents!');
      }
    }
    else if (memberId) {
      openFeatureWall({
        cid: null,
        title: 'Unlock Print All',
        text: 'Print All is available to Premium members only. Upgrade to unlock the feature.'
      });
    }
    else {
      openSignup('print-all');
    }
  }

  const handleAddToAssignments = () => {
    if (!ready) {
      return;
    }

    if (isPremium) {
      //modify selected list based on content type
      let selectedFormatted = [];
      const assignableTypes = ['game', 'song', 'story', 'worksheet', 'skill-builder'];
      selected.forEach(content => {
        if (content.type === 'guided-lesson' && content.meta.exercises) {
          content.meta.exercises.forEach(exercise => {
            selectedFormatted.push({
              type: 'exercise',
              internalName: exercise,
            });
          });
        } else {
          if (assignableTypes.includes(content.type)) {
            selectedFormatted.push({
              type: content.type !== 'worksheet' ? 'exercise' : 'worksheet',
              internalName: String(content.id),
            });
          }
        }
      });

      if (selectedFormatted.length === 0) {
        alert('Nothing is assignable in your selected contents!');
      } else {
        setAssignmentsModalIsOpen(true);
        setAssignmentsToAdd(selectedFormatted);
      }
    } else if (memberId) {
      openFeatureWall({
        // Remove the unused&buggy cid tracking, the value used was 11.999.
        cid: null,
        title: 'Unlock Assignments',
        text: 'Assignments are available to Premium members only. Upgrade to assign worksheets, games, and more to your child.'
      });
    } else {
      openSignup('assign');
    }
  };

  const handleActions = action => {
    switch (action) {
      case 'print-all': {
        handlePrintAll();
        break;
      }
      case 'add-to-collection': {
        setModalIsOpen(true);
        setSelectedContent(selected);
        break;
      }
      case 'add-to-assignment': {
        handleAddToAssignments();
        break;
      }
    }

    // reset to "action"
    document.querySelectorAll('#ms-actions option')[0].selected = true;
  };

  const selectStyle = {
    border: '3px solid #5672c4',
    height: '40px',
    boxShadow:
      '0 1px 3px 0 rgba(0, 0, 0, 0.12), 0 1px 2px 0 rgba(0, 0, 0, 0.24)',
  };

  return (
    <div className={styles.multiSelectionActions}>
      <Tooltip
        text={'Click here for printing, Assignments, and Collections.'}
        localStorageKey={'haveShownActionsTooltip'}
        hideAfterInMilliseconds={9500}
        style={{
          container: styles.ttContainer,
          box: styles.ttBox,
          triangle: styles.ttTriangle,
        }}
      />

      <div className="form sorting-options">
        <div className="input-row">
          <select
            id="ms-actions"
            className="select-value ms-actions"
            style={selectStyle}
            onChange={e => {
              handleActions(e.target.value);
            }}
          >
            <option>Actions</option>
            <option value="print-all">Print All</option>
            <option value="add-to-assignment">Add to Assignment</option>
            <option value="add-to-collection">Add to Collection</option>
          </select>
        </div>
      </div>

      <AssignmentsModal
        onClose={() => setAssignmentsModalIsOpen(false)}
        isOpen={assignmentsModalIsOpen}
        itemsToAdd={assignmentsToAdd}
      />
    </div>
  );
}

MultiSelectActions.propTypes = {
  selected: PropTypes.array,
};
